import { Component, OnInit } from '@angular/core';
import { PfsService } from 'src/app/services/pfs.service';
import { ChartType } from 'angular-google-charts';

interface ChartDefinition {
  chartType: ChartType;
  chartColumns: any[];
  chartData: any[];
  chartOptions: any;
}

@Component({
  selector: 'app-pfs-client-charts',
  templateUrl: './pfs-client-charts.component.html',
  styleUrls: ['./pfs-client-charts.component.css']
})
export class PfsClientChartsComponent implements OnInit {

  chartDefinitions: ChartDefinition[] = [];
  chartReady = false;

  constructor(private pfsService: PfsService) { }

  ngOnInit(): void {
    const clientId = 99;
    const token = '';
    this.loadCharts(clientId, token);
  }

  loadCharts(clientId: number, token?: string): void {
    const chartTypeMap: { [key: string]: ChartType } = {
      piechart: ChartType.PieChart,
      columnchart: ChartType.ColumnChart,
      barchart: ChartType.BarChart,
      linechart: ChartType.LineChart,
      areachart: ChartType.AreaChart,
      combochart: ChartType.ComboChart,
      table: ChartType.Table
    };

    this.pfsService.getChartConfigByClient(clientId, token).subscribe(configs => {
      const chartPromises = configs.map((config: any) => {
        const rawType = (config.chartType || '').toLowerCase();
        const normalizedType: ChartType = chartTypeMap[rawType] || config.chartType;
        const paramCols = JSON.parse(config.parameterColumns || '{}');
        const dataSet = config.dataSet;

        return this.pfsService
          .getChartDataByMetadata(clientId, dataSet, paramCols.x, paramCols.y, token)
          .toPromise()
          .then(data => {
            if (!data) {
              console.warn('No chart data returned for config:', config);
              return null;
            }

            return this.buildChartDefinition(normalizedType, config, data, paramCols);
          })
          .catch(err => {
            console.error(`Failed to fetch data for chart`, config, err);
            return null;
          });
      });

      Promise.all(chartPromises).then(results => {
        this.chartDefinitions = results.filter(def => def !== null);
        this.chartReady = true;
      });
    });
  }


  buildChartDefinition(
    chartType: ChartType,
    config: any,
    rawData: any[],
    paramCols: { x: string; y: string }
  ): ChartDefinition {
    const xKey = paramCols.x;
    const yKey = paramCols.y;

    let chartColumns: any[] = [];
    let chartData: any[] = [];
    let chartOptions: any = {};

    switch (chartType) {
      case 'PieChart': {
        chartColumns = [['string', this.titleCase(xKey)], ['number', this.titleCase(yKey)]];
        chartData = this.groupAndSum(rawData, xKey, yKey);
        chartOptions = {
          title: config.title || 'Pie Chart',
          is3D: true,
          pieSliceText: config.showTotal ? 'value-and-percentage' : 'percentage',
          legend: { position: 'right' },
          ...config.options
        };
        break;
      }

      case 'ColumnChart':
      case 'BarChart':
      case 'LineChart': {
        chartColumns = [['string', this.titleCase(xKey)], ['number', this.titleCase(yKey)]];
        chartData = rawData.map(row => [row[xKey], row[yKey]]);
        chartOptions = {
          title: config.title || chartType,
          hAxis: { title: this.titleCase(xKey) },
          vAxis: { title: this.titleCase(yKey) },
          legend: { position: 'top' },
          ...config.options
        };
        break;
      }

      default:
        console.warn(`Unsupported chart type: ${chartType}`);
        return {
          chartType,
          chartColumns: [],
          chartData: [],
          chartOptions: { title: `Unsupported chart type: ${chartType}` }
        };
    }

    return { chartType, chartColumns, chartOptions, chartData };
  }

  groupAndSum(data: any[], xKey: string, yKey: string): [string, number][] {
    const grouped: { [key: string]: number } = {};

    data.forEach(row => {
      const x = row[xKey];
      const y = Number(row[yKey]) || 0;
      if (x !== undefined && x !== null) {
        grouped[x] = (grouped[x] || 0) + y;
      } else {
        console.warn(`Missing key '${xKey}' in row:`, row);
      }
    });

    return Object.entries(grouped).map(([key, val]) => [key, val]);
  }

  titleCase(str: string): string {
    return str
      .replace(/_/g, ' ')
      .replace(/\w\S*/g, txt => txt[0].toUpperCase() + txt.substring(1).toLowerCase());
  }
}
